// TODO: Codes related to showing flag is commented because this view is currently wrong and asked to hide this view and keep code for future
import { TextLayer } from '@deck.gl/layers';

import type { ColorRGB, TrainRenderData } from '@/types';

import { JOURNEY_STATE } from '@/utils/constants';
import {
  characterSet,
  COLOR_FLAG_BLUE,
  COLOR_GRAY,
  COLOR_RED,
  COLOR_SIGNED_ON,
  COLOR_WHITE,
  DEPTH,
  DEVIATION_BADGE_BG_COLORS,
  DEVIATION_BADGE_LAYER,
  DEVIATION_BADGE_TEXT_COLORS,
  DIRECTION_TORCH_LAYER,
  FLAG_BOTTOM_LAYER,
  FLAG_TOP_LAYER,
  HUD_PARAMETERS,
  ICON_SIZE,
  JOURNEY_NUMBER_BADGE_LAYER,
  LOCKED_CIRCLE_LAYER,
  LOCKED_PIN_BG_LAYER,
  LOCKED_PIN_BORDER_LAYER,
  LOCKED_PIN_ICON_LAYER,
  MAPPINGS,
  OFFSET_CIRCLE,
  OFFSET_DEVIATION,
  OFFSET_JOURNEY,
  OFFSET_PIN_X,
  OFFSET_PIN_Y,
  OFFSET_TORCH,
  PIN_H,
  POLYGON_OFFSET_TEXT,
  TEXT_STYLE,
  TORCH_SIZE,
  TRAIN_CIRCLE_BORDER_LAYER,
  TRAIN_CIRCLE_LAYER,
  TRAIN_CIRCLE_TEXT_LAYER,
  TRAIN_DIAMETER_LARGE,
  TRAIN_DIAMETER_SMALL,
  ZOOM_11,
  ZOOM_13,
} from '@/utils/constants/map';
import { getJourneyDisplayName } from '@/utils/helpers/get-journey-display-name';
import { createBadgeGroup } from '@/utils/helpers/train-simulation/create-badge-group';
import { FLAG_CANVAS_HEIGHT } from '@/utils/helpers/train-simulation/create-flag-atlas';
import { getAtlases } from '@/utils/helpers/train-simulation/get-atlases';
import { makeIconLayer } from '@/utils/helpers/train-simulation/make-icon-layer';

const STARTING_STATES = [JOURNEY_STATE.SIGNED_ON, JOURNEY_STATE.PENDING];

const getPosition = (d: TrainRenderData): ColorRGB => d.position as unknown as ColorRGB;
const getTrainColor = (d: TrainRenderData): ColorRGB => {
  if (d.journeyState === JOURNEY_STATE.SIGNED_ON) {
    return COLOR_SIGNED_ON;
  }

  if (d.journeyFinished) {
    return COLOR_GRAY;
  }

  return d.color;
};
const getTextColor = (d: TrainRenderData): ColorRGB => {
  if (d.journeyState === JOURNEY_STATE.SIGNED_ON) {
    return COLOR_GRAY;
  }

  return COLOR_WHITE;
};
const getBearingAngle = (d: TrainRenderData) => -d.bearing;
const getJourneyNumber = (d: TrainRenderData) =>
  d.hasActiveDangerousGoods ? `      ${d.journeyNumber}` : d.journeyNumber;

const getDeviationTime = (d: TrainRenderData) => d.deviationTime;
const getDeviationBgColor = ({ deviationCategory }: TrainRenderData) =>
  DEVIATION_BADGE_BG_COLORS[deviationCategory] ?? DEVIATION_BADGE_BG_COLORS.default;
const getDeviationTextColor = ({ deviationCategory }: TrainRenderData) =>
  DEVIATION_BADGE_TEXT_COLORS[deviationCategory] ?? DEVIATION_BADGE_TEXT_COLORS.default;

export const getTrainDeckLayers = (
  rawData: TrainRenderData[],
  zoom: number,
  lockedId: string | number | null
  // flaggedTrainIds?: Set<string> | null
) => {
  const atlases = getAtlases();

  if (!atlases) {
    return [];
  }

  const isLarge = zoom >= ZOOM_11;
  const showDeviation = zoom >= ZOOM_13;

  const singleData: TrainRenderData[] = [];
  const lockedData: TrainRenderData[] = [];
  const deviationData: TrainRenderData[] = [];
  const activeTorchData: TrainRenderData[] = [];
  const flagData: TrainRenderData[] = [];
  const dangerousData: TrainRenderData[] = [];

  const unlockedSafeData: TrainRenderData[] = [];
  const unlockedDangerData: TrainRenderData[] = [];
  const lockedSafeData: TrainRenderData[] = [];
  const lockedDangerData: TrainRenderData[] = [];

  for (let i = 0, len = rawData.length; i < len; i++) {
    const item = rawData[i];

    if (!item.id) {
      continue;
    }

    singleData.push(item);

    const isFinishedOrStarting =
      item.journeyFinished || item.willStart || STARTING_STATES.includes(item.journeyState);

    if (!isFinishedOrStarting) {
      activeTorchData.push(item);
    }

    // if (flaggedTrainIds?.has(String(item.id))) {
    //   flagData.push(item);
    // }

    if (item.hasActiveDangerousGoods) {
      dangerousData.push(item);
    }

    if (lockedId === String(item.id)) {
      lockedData.push(item);

      if (item.hasActiveDangerousGoods) {
        lockedDangerData.push(item);
      } else {
        lockedSafeData.push(item);
      }
    } else {
      if (item.hasActiveDangerousGoods) {
        unlockedDangerData.push(item);
      } else {
        unlockedSafeData.push(item);
      }

      if (showDeviation && !isFinishedOrStarting) {
        deviationData.push(item);
      }
    }
  }

  const baseCircleProps = {
    data: singleData,
    atlas: atlases.circle,
    mapping: MAPPINGS.CIRCLE,
    icon: 'circle',
    offset: OFFSET_CIRCLE,
    getPosition,
  };

  const baseLockedProps = {
    data: lockedData,
    offset: (isLarge ? [OFFSET_PIN_X, OFFSET_PIN_Y] : [16, -16]) as [number, number],
    getPosition,
  };

  const flagProps = {
    data: flagData,
    offset: (isLarge ? [12, -44] : [6, -16]) as [number, number],
    size: (FLAG_CANVAS_HEIGHT / 2) * (isLarge ? 1 : 0.6),
    getPosition,
  };

  return [
    ...createBadgeGroup(
      JOURNEY_NUMBER_BADGE_LAYER,
      isLarge ? unlockedSafeData : [],
      atlases.journey,
      MAPPINGS.JOURNEY,
      OFFSET_JOURNEY,
      DEPTH.BADGE,
      getPosition,
      COLOR_GRAY,
      getJourneyNumber
    ),

    ...createBadgeGroup(
      `${JOURNEY_NUMBER_BADGE_LAYER}-dangerous`,
      isLarge ? unlockedDangerData : [],
      atlases.journeyWithDangerousWagon,
      MAPPINGS.JOURNEY_WITH_DANGEROUS_WAGON,
      OFFSET_JOURNEY,
      DEPTH.BADGE,
      getPosition,
      COLOR_GRAY,
      getJourneyNumber
    ),

    makeIconLayer({
      id: `${JOURNEY_NUMBER_BADGE_LAYER}-pin-icon-both`,
      data: dangerousData,
      atlas: atlases.dangerMarker,
      mapping: MAPPINGS.DANGEROUS_MARKER,
      offset: [-22, 23],
      size: 14,
      color: COLOR_GRAY,
      depth: DEPTH.TEXT - 1,
      getPosition,
      visible: isLarge,
    }),

    ...createBadgeGroup(
      DEVIATION_BADGE_LAYER,
      deviationData,
      atlases.deviation,
      MAPPINGS.DEVIATION,
      OFFSET_DEVIATION,
      DEPTH.BADGE,
      getPosition,
      getDeviationBgColor,
      getDeviationTime,
      getDeviationTextColor
    ),

    makeIconLayer({
      ...baseCircleProps,
      id: TRAIN_CIRCLE_BORDER_LAYER,
      color: COLOR_WHITE,
      depth: DEPTH.CIRCLE + 0.1,
      size: isLarge ? 46 : 15,
      pickable: true,
    }),

    makeIconLayer({
      ...baseCircleProps,
      id: TRAIN_CIRCLE_LAYER,
      color: getTrainColor,
      depth: DEPTH.CIRCLE,
      size: isLarge ? TRAIN_DIAMETER_LARGE : TRAIN_DIAMETER_SMALL,
    }),

    new TextLayer<TrainRenderData>({
      id: TRAIN_CIRCLE_TEXT_LAYER,
      data: singleData,
      visible: isLarge,
      pickable: false,
      getPosition,
      getPixelOffset: OFFSET_CIRCLE,
      getText: getJourneyDisplayName,
      getSize: TEXT_STYLE.size,
      fontWeight: TEXT_STYLE.fontWeight,
      fontFamily: TEXT_STYLE.fontFamily,
      getColor: getTextColor,
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'center',
      billboard: true,
      parameters: HUD_PARAMETERS,
      characterSet: characterSet,
      getPolygonOffset: POLYGON_OFFSET_TEXT,
    }),

    makeIconLayer({
      ...baseLockedProps,
      id: LOCKED_CIRCLE_LAYER,
      atlas: atlases.ring,
      mapping: MAPPINGS.RING,
      icon: 'circle',
      offset: OFFSET_CIRCLE,
      size: isLarge ? 56 : 20,
      color: COLOR_RED,
      depth: DEPTH.CIRCLE + 0.1,
    }),

    ...createBadgeGroup(
      `${JOURNEY_NUMBER_BADGE_LAYER}-locked`,
      isLarge ? lockedSafeData : [],
      atlases.journey,
      MAPPINGS.JOURNEY,
      OFFSET_JOURNEY,
      DEPTH.BADGE,
      getPosition,
      COLOR_GRAY,
      getJourneyNumber
    ),

    ...createBadgeGroup(
      `${JOURNEY_NUMBER_BADGE_LAYER}-locked-dangerous`,
      isLarge ? lockedDangerData : [],
      atlases.journeyWithDangerousWagon,
      MAPPINGS.JOURNEY_WITH_DANGEROUS_WAGON,
      OFFSET_JOURNEY,
      DEPTH.BADGE,
      getPosition,
      COLOR_GRAY,
      getJourneyNumber
    ),

    makeIconLayer({
      ...baseLockedProps,
      id: LOCKED_PIN_BORDER_LAYER,
      atlas: atlases.pinShape,
      mapping: MAPPINGS.PIN_SHAPE,
      size: isLarge ? PIN_H + 3 : 20,
      color: COLOR_WHITE,
      depth: DEPTH.BADGE + 0.1,
    }),

    makeIconLayer({
      ...baseLockedProps,
      id: LOCKED_PIN_BG_LAYER,
      atlas: atlases.pinShape,
      mapping: MAPPINGS.PIN_SHAPE,
      size: isLarge ? PIN_H : 18,
      color: COLOR_RED,
      depth: DEPTH.BADGE,
    }),

    makeIconLayer({
      ...baseLockedProps,
      id: LOCKED_PIN_ICON_LAYER,
      atlas: atlases.pinIcon,
      mapping: MAPPINGS.PIN_ICON,
      size: isLarge ? ICON_SIZE : 14,
      color: COLOR_WHITE,
      depth: DEPTH.TEXT,
    }),

    makeIconLayer({
      ...flagProps,
      id: FLAG_BOTTOM_LAYER,
      atlas: atlases.flagBottom,
      mapping: MAPPINGS.FLAG_BOTTOM,
      color: COLOR_FLAG_BLUE,
      depth: DEPTH.BADGE + 0.2,
    }),

    makeIconLayer({
      ...flagProps,
      id: FLAG_TOP_LAYER,
      atlas: atlases.flagTop,
      mapping: MAPPINGS.FLAG_TOP,
      color: COLOR_WHITE,
      depth: DEPTH.BADGE + 0.3,
    }),

    makeIconLayer({
      id: DIRECTION_TORCH_LAYER,
      data: activeTorchData,
      atlas: atlases.torch,
      mapping: MAPPINGS.TORCH,
      offset: OFFSET_TORCH,
      size: TORCH_SIZE,
      color: (d: TrainRenderData) => d.color,
      depth: DEPTH.TORCH,
      visible: isLarge,
      angle: getBearingAngle,
      getPosition,
      billboard: false,
    }),
  ];
};
